<template>
  <div class="min-h-screen bg-gray-50">
    <PageHeader
      title="Sectoriser vos ventes"
      subtitle="Cochez les zones où vous ne souhaitez PAS vendre"
      :show-save="true"
      :show-export="true"
    >
      <template #actions>
        <div class="hidden xl:flex items-center gap-4">
          <ActionButton
            label="Exporter"
            icon="fas fa-download"
            variant="secondary"
            @click="exportData"
          />
        </div>
      </template>
    </PageHeader>

    <div class="xl:hidden bg-white border-b border-gray-200 px-6 py-4">
      <ActionButton
        label="Exporter"
        icon="fas fa-download"
        variant="secondary"
        @click="exportData"
      />
    </div>

    <div class="w-full sm:px-6 py-8 space-y-6">
      <DataCard
        title="Recherche et filtres"
        icon="fas fa-search"
        icon-color="text-blue-600"
        icon-bg-color="bg-blue-100"
        class="w-full"
      >
        <div class="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div class="flex-1 max-w-md">
            <BaseInput
              v-model.trim="searchQuery"
              type="text"
              placeholder="Rechercher un pays à exclure..."
              icon="fas fa-search"
            />
          </div>
          <div class="flex flex-wrap items-center gap-4">
            <ActionButton label="Tout exclure" icon="fas fa-ban" variant="secondary" @click="selectAllCountries" />
            <ActionButton label="Tout réautoriser" icon="fas fa-check" variant="secondary" @click="deselectAllCountries" />
            <ActionButton label="Recharger" icon="fas fa-sync-alt" variant="secondary" :loading="isLoading" @click="loadData" />
          </div>
        </div>
      </DataCard>

      <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div class="flex items-center">
            <i class="fas fa-ban text-red-600 mr-2"></i>
            <span class="text-sm font-medium text-black">Zones exclues :</span>
          </div>
          <div class="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <span class="text-blue-600"><i class="fas fa-globe mr-1"></i>{{ excludedCountriesCount }} pays</span>
            <span class="text-green-600"><i class="fas fa-flag mr-1"></i>{{ excludedDomTomCount }} DOM/TOM</span>
            <span class="text-red-600"><i class="fas fa-map-marker-alt mr-1"></i>{{ excludedDepartmentsCount }} départements</span>
          </div>
        </div>
        <span v-if="isAutoSaving" class="mt-3 text-blue-600 text-sm">
          <i class="fas fa-spinner fa-spin"></i> Sauvegarde...
        </span>
      </div>

      <DataCard
        title="Pays à exclure"
        icon="fas fa-globe-americas"
        icon-color="text-blue-600"
        icon-bg-color="bg-blue-100"
        class="w-full"
      >
        <div v-if="isLoading" class="flex items-center justify-center py-8 text-black">
          <i class="fas fa-spinner fa-spin mr-3 text-blue-600"></i>
          Chargement des pays...
        </div>

        <div v-else class="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div
            v-for="country in filteredCountries"
            :key="country.id_pays"
            class="flex items-center p-3 border rounded-lg transition-colors"
            :class="zoneItemClass(isCountryExcluded(country.id_pays))"
          >
            <input
              :id="'country-' + country.id_pays"
              type="checkbox"
              class="w-4 h-4 flex-shrink-0 text-red-600 border-gray-300 rounded focus:ring-red-500"
              :checked="isCountryExcluded(country.id_pays)"
              @change="toggleCountry(country.id_pays)"
            >
            <label
              :for="'country-' + country.id_pays"
              class="ml-3 text-sm cursor-pointer flex-1 truncate"
              :class="zoneLabelClass(isCountryExcluded(country.id_pays))"
            >
              {{ country.name }}
              <span v-if="isCountryExcluded(country.id_pays)" class="ml-1 text-xs text-red-600">(exclu)</span>
            </label>
          </div>
        </div>

        <div v-if="!isLoading && filteredCountries.length === 0" class="text-center py-8 text-black">
          Aucun pays trouvé
        </div>
      </DataCard>

      <DataCard
        title="France DOM/TOM"
        icon="fas fa-flag"
        icon-color="text-green-600"
        icon-bg-color="bg-green-100"
        class="w-full"
      >
        <div
          class="flex items-center p-3 mb-4 border rounded-lg transition-colors"
          :class="zoneItemClass(isDomTomExcluded)"
        >
          <input
            id="pays-domtom"
            type="checkbox"
            class="w-4 h-4 flex-shrink-0 text-red-600 border-gray-300 rounded focus:ring-red-500"
            :checked="isDomTomExcluded"
            :disabled="isAutoSaving"
            @change="toggleCountry(DOM_TOM_PAYS_ID)"
          >
          <label
            for="pays-domtom"
            class="ml-3 text-sm font-medium cursor-pointer"
            :class="zoneLabelClass(isDomTomExcluded)"
          >
            France DOM/TOM
            <span v-if="isDomTomExcluded" class="ml-1 text-xs font-normal text-red-600">(exclu)</span>
          </label>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="region in domTomRegions"
            :key="region.code"
            class="flex items-center p-3 border rounded-lg transition-colors overflow-hidden"
            :class="zoneItemClass(isDomTomCodeExcluded(region.code), isDomTomExcluded)"
          >
            <input
              :id="'domtom-' + region.code"
              type="checkbox"
              class="w-4 h-4 flex-shrink-0 text-red-600 border-gray-300 rounded focus:ring-red-500"
              :checked="isDomTomExcluded || isDomTomCodeExcluded(region.code)"
              :disabled="isDomTomExcluded || isAutoSaving"
              @change="toggleDomTom(region.code)"
            >
            <label
              :for="'domtom-' + region.code"
              class="ml-3 text-sm cursor-pointer flex-1 break-words"
              :class="zoneLabelClass(isDomTomCodeExcluded(region.code), isDomTomExcluded)"
            >
              {{ region.name }} ({{ region.code }})
              <span
                v-if="!isDomTomExcluded && isDomTomCodeExcluded(region.code)"
                class="ml-1 text-xs text-red-600"
              >(exclu)</span>
            </label>
          </div>
        </div>
      </DataCard>

      <DataCard
        title="France métropolitaine"
        icon="fas fa-map-marker-alt"
        icon-color="text-red-600"
        icon-bg-color="bg-red-100"
        class="w-full"
      >
        <div
          class="flex items-center p-3 mb-4 border rounded-lg transition-colors"
          :class="zoneItemClass(isFranceExcluded)"
        >
          <input
            id="pays-france"
            type="checkbox"
            class="w-4 h-4 flex-shrink-0 text-red-600 border-gray-300 rounded focus:ring-red-500"
            :checked="isFranceExcluded"
            :disabled="isAutoSaving"
            @change="toggleCountry(FRANCE_PAYS_ID)"
          >
          <label
            for="pays-france"
            class="ml-3 text-sm font-medium cursor-pointer"
            :class="zoneLabelClass(isFranceExcluded)"
          >
            France métropolitaine
            <span v-if="isFranceExcluded" class="ml-1 text-xs font-normal text-red-600">(exclu)</span>
          </label>
        </div>

        <div class="mb-6 max-w-md">
          <BaseInput
            v-model.trim="departmentSearch"
            type="text"
            placeholder="Rechercher un département à exclure..."
            icon="fas fa-search"
          />
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div
            v-for="dept in filteredDepartments"
            :key="dept.code"
            class="flex items-center p-3 border rounded-lg transition-colors"
            :class="zoneItemClass(isDepartmentExcluded(dept.code), isFranceExcluded)"
          >
            <input
              :id="'dept-' + dept.code"
              type="checkbox"
              class="w-4 h-4 flex-shrink-0 text-red-600 border-gray-300 rounded focus:ring-red-500"
              :checked="isFranceExcluded || isDepartmentExcluded(dept.code)"
              :disabled="isFranceExcluded || isAutoSaving"
              @change="toggleDepartment(dept.code)"
            >
            <label
              :for="'dept-' + dept.code"
              class="ml-3 text-sm cursor-pointer flex-1"
              :class="zoneLabelClass(isDepartmentExcluded(dept.code), isFranceExcluded)"
            >
              {{ dept.name }} ({{ dept.code }})
              <span
                v-if="!isFranceExcluded && isDepartmentExcluded(dept.code)"
                class="ml-1 text-xs text-red-600"
              >(exclu)</span>
            </label>
          </div>
        </div>
      </DataCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { PageHeader, DataCard, BaseInput, ActionButton } from '@/components'
import { getAllCountries, getVendorSectorization, saveSalesSectorization } from '@/api/salesSectorization'
import { useNotification } from '@/composables/useNotification'
import { useAuthStore } from '@/stores/auth'
import { DOM_TOM_REGIONS } from '@/utils/domTom'
import { FRANCE_DEPARTMENTS } from '@/utils/frenchDepartments'
import type { SalesData } from '@/types'
import type { SectorizationCountry } from '@/types/salesSectorization'

const FRANCE_PAYS_ID = 10
const DOM_TOM_PAYS_ID = 42
const SPECIAL_PAYS_IDS = new Set([FRANCE_PAYS_ID, DOM_TOM_PAYS_ID])

const ALLOWED_COUNTRY_CODES = new Set([
  'ZA', 'DZ', 'DE', 'AD', 'AO', 'SA', 'AR', 'AU', 'AT', 'BH', 'BD', 'BB', 'BE', 'BJ', 'BO', 'BA', 'BW', 'BR', 'BG', 'BF', 'BI', 'CM', 'CA', 'CL', 'CY', 'CO', 'KM', 'CG', 'KR', 'CR', 'CI', 'HR', 'DK', 'EG', 'AE', 'EC', 'ES', 'EE', 'US', 'FI', 'GA', 'GE', 'GH', 'GR', 'GN', 'GQ', 'HN', 'HU', 'MU', 'IN', 'IQ', 'IE', 'IS', 'IL', 'IT', 'JE', 'KE', 'XK', 'LV', 'LB', 'LI', 'LT', 'LU', 'MG', 'ML', 'MT', 'MA', 'MR', 'MX', 'MC', 'ME', 'NA', 'NE', 'NG', 'NO', 'NZ', 'PA', 'NL', 'PE', 'PH', 'PL', 'PR', 'PT', 'QA', 'CF', 'CD', 'CZ', 'RO', 'GB', 'RU', 'RW', 'MF', 'SN', 'RS', 'SG', 'SK', 'SI', 'SE', 'CH', 'TW', 'TD', 'TG', 'TT', 'TN', 'TR', 'UA', 'UY', 'VE',
])

const searchQuery = ref('')
const departmentSearch = ref('')
const isLoading = ref(false)
const isAutoSaving = ref(false)
const countries = ref<SectorizationCountry[]>([])
const salesData = reactive<SalesData>({
  selectedCountries: [],
  selectedDomTom: [],
  selectedDepartments: [],
})

const { error } = useNotification()
const authStore = useAuthStore()
const departments = FRANCE_DEPARTMENTS
const domTomRegions = DOM_TOM_REGIONS
const deptCodeSet = new Set(departments.map((d) => d.code))
const domTomCodeSet = new Set(domTomRegions.map((r) => r.code))

const isFranceExcluded = computed(() => salesData.selectedCountries.includes(FRANCE_PAYS_ID))
const isDomTomExcluded = computed(() => salesData.selectedCountries.includes(DOM_TOM_PAYS_ID))
const excludedCountriesCount = computed(
  () => salesData.selectedCountries.filter((id) => !SPECIAL_PAYS_IDS.has(id)).length,
)
const excludedDomTomCount = computed(() =>
  isDomTomExcluded.value ? domTomRegions.length : salesData.selectedDomTom.length,
)
const excludedDepartmentsCount = computed(() =>
  isFranceExcluded.value ? departments.length : salesData.selectedDepartments.length,
)

const filteredCountries = computed(() => {
  const q = searchQuery.value.toLowerCase()
  if (!q) return countries.value
  return countries.value.filter((c) => (c.name ?? '').toLowerCase().includes(q))
})

const filteredDepartments = computed(() => {
  const q = departmentSearch.value.toLowerCase()
  if (!q) return departments
  return departments.filter((d) => d.name.toLowerCase().includes(q) || d.code.includes(departmentSearch.value))
})

const isCountryExcluded = (id: number) => salesData.selectedCountries.includes(id)
const isDomTomCodeExcluded = (code: string) => salesData.selectedDomTom.includes(code)
const isDepartmentExcluded = (code: string) => salesData.selectedDepartments.includes(code)

const zoneItemClass = (isExcluded: boolean, parentExcluded = false) => {
  if (parentExcluded) return 'opacity-50 border-gray-200 bg-gray-50'
  if (isExcluded) return 'border-red-300 bg-red-50'
  return 'border-gray-200 hover:bg-gray-50'
}

const zoneLabelClass = (isExcluded: boolean, parentExcluded = false) => {
  if (parentExcluded) return 'text-gray-500'
  if (isExcluded) return 'text-red-800 font-medium'
  return 'text-black'
}

function toggleInList<T>(list: T[], value: T) {
  const index = list.indexOf(value)
  if (index > -1) list.splice(index, 1)
  else list.push(value)
}

const keptSpecialPays = () =>
  salesData.selectedCountries.filter((id) => SPECIAL_PAYS_IDS.has(id))

const toggleCountry = async (countryId: number) => {
  toggleInList(salesData.selectedCountries, countryId)
  if (salesData.selectedCountries.includes(countryId)) {
    if (countryId === FRANCE_PAYS_ID) salesData.selectedDepartments = []
    if (countryId === DOM_TOM_PAYS_ID) salesData.selectedDomTom = []
  }
  await autoSave()
}

const toggleDomTom = async (code: string) => {
  if (isDomTomExcluded.value) return
  toggleInList(salesData.selectedDomTom, code)
  await autoSave()
}

const toggleDepartment = async (code: string) => {
  if (isFranceExcluded.value) return
  toggleInList(salesData.selectedDepartments, code)
  await autoSave()
}

const selectAllCountries = async () => {
  salesData.selectedCountries = [
    ...new Set([...countries.value.map((c) => c.id_pays), ...keptSpecialPays()]),
  ]
  await autoSave()
}

const deselectAllCountries = async () => {
  salesData.selectedCountries = keptSpecialPays()
  await autoSave()
}

const autoSave = async () => {
  if (isAutoSaving.value || !authStore.isAuthenticated) return
  isAutoSaving.value = true
  try {
    const result = await saveSalesSectorization(
      salesData.selectedCountries,
      salesData.selectedDomTom,
      salesData.selectedDepartments,
    )
    if (!result.success) error('Erreur lors de la sauvegarde automatique')
  } catch {
    error('Erreur lors de la sauvegarde automatique')
  } finally {
    isAutoSaving.value = false
  }
}

const exportData = () => {
  const blob = new Blob(
    [JSON.stringify({
      countries: salesData.selectedCountries,
      domTom: salesData.selectedDomTom,
      departments: salesData.selectedDepartments,
      exportDate: new Date().toISOString(),
    }, null, 2)],
    { type: 'application/json' },
  )
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `sectorisation-${new Date().toISOString().split('T')[0]}.json`
  link.click()
  URL.revokeObjectURL(url)
}

const loadData = async () => {
  isLoading.value = true
  try {
    const [countriesData, vendorData] = await Promise.all([
      getAllCountries(),
      getVendorSectorization(),
    ])

    countries.value = countriesData
      .filter((country) => ALLOWED_COUNTRY_CODES.has(country.code))
      .map((country) => ({
        ...country,
        id_pays: Number(country.id_pays),
        name: country.texte_fr,
      }))

    const allowedPaysIds = new Set([
      ...countries.value.map((c) => c.id_pays),
      FRANCE_PAYS_ID,
      DOM_TOM_PAYS_ID,
    ])
    // Les anciennes sectorisations peuvent être rattachées à une marque.
    // Elles restent des exclusions existantes et doivent donc être visibles ici.
    const secteurs = vendorData.secteurs ?? []

    salesData.selectedCountries = [
      ...new Set(
        secteurs
          .filter((s) => !s.codePostal && allowedPaysIds.has(Number(s.id_pays)))
          .map((s) => Number(s.id_pays)),
      ),
    ]
    salesData.selectedDepartments = [
      ...new Set(
        secteurs
          .filter((s) => Number(s.id_pays) === FRANCE_PAYS_ID && s.codePostal && deptCodeSet.has(String(s.codePostal)))
          .map((s) => String(s.codePostal)),
      ),
    ]
    salesData.selectedDomTom = [
      ...new Set(
        secteurs
          .filter((s) => Number(s.id_pays) === DOM_TOM_PAYS_ID && s.codePostal && domTomCodeSet.has(String(s.codePostal)))
          .map((s) => String(s.codePostal)),
      ),
    ]

    if (isFranceExcluded.value) salesData.selectedDepartments = []
    if (isDomTomExcluded.value) salesData.selectedDomTom = []
  } catch {
    error('Erreur lors du chargement des données. Veuillez réessayer.')
  } finally {
    isLoading.value = false
  }
}

onMounted(loadData)
</script>
